import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Button,
  Badge,
  ConfirmDialog,
  EmptyState,
  Spinner,
} from '../components/ui';
import { AddOfferingModal } from '../components/modals/AddOfferingModal';
import { useOfferingStore } from '../stores/offeringStore';
import type { Offering, CreateOfferingData } from '../types';
import { formatCurrency } from '../utils/helpers';

interface OfferingsByCompound {
  compoundId: string;
  compoundName: string;
  offerings: Offering[];
  activeCount: number;
}

export const Medications: React.FC = () => {
  const {
    compounds,
    offerings,
    isLoadingCompounds,
    isLoadingOfferings,
    isUpdating,
    isDeleting,
    fetchCompounds,
    fetchOfferings,
    createOffering,
    updateOffering,
    toggleActive,
    deleteOffering,
  } = useOfferingStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOffering, setEditingOffering] = useState<Offering | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offeringToDelete, setOfferingToDelete] = useState<Offering | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchCompounds();
    fetchOfferings();
  }, [fetchCompounds, fetchOfferings]);

  // Group offerings by compound
  const offeringsByCompound = useMemo<OfferingsByCompound[]>(() => {
    const grouped: Record<string, OfferingsByCompound> = {};

    offerings.forEach((offering) => {
      if (!grouped[offering.compoundId]) {
        const compound = compounds.find(c => c.id === offering.compoundId);
        grouped[offering.compoundId] = {
          compoundId: offering.compoundId,
          compoundName: compound?.name || 'Unknown Compound',
          offerings: [],
          activeCount: 0,
        };
      }
      grouped[offering.compoundId].offerings.push(offering);
      if (offering.isActive) {
        grouped[offering.compoundId].activeCount++;
      }
    });

    return Object.values(grouped).sort((a, b) => a.compoundName.localeCompare(b.compoundName));
  }, [offerings, compounds]);

  const toggleSection = (compoundId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [compoundId]: !prev[compoundId]
    }));
  };

  const isSectionCollapsed = (compoundId: string) => {
    return collapsedSections[compoundId] === true;
  };

  const handleAddOffering = () => {
    setEditingOffering(null);
    setIsModalOpen(true);
  };

  const handleEditOffering = (offering: Offering) => {
    setEditingOffering(offering);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingOffering(null);
  };

  const handleSubmit = async (data: CreateOfferingData) => {
    try {
      if (editingOffering) {
        await updateOffering(editingOffering.id, data);
        toast.success('Offering updated successfully');
      } else {
        await createOffering(data);
        toast.success('Offering created successfully');
      }
      handleCloseModal();
    } catch (error) {
      toast.error(editingOffering ? 'Failed to update offering' : 'Failed to create offering');
      throw error;
    }
  };

  const handleToggleActive = async (offering: Offering) => {
    try {
      await toggleActive(offering.id);
      toast.success(`Offering ${offering.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      toast.error('Failed to update offering status');
    }
  };

  const handleDeleteClick = (offering: Offering) => {
    setOfferingToDelete(offering);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!offeringToDelete) return;
    try {
      await deleteOffering(offeringToDelete.id);
      toast.success('Offering deleted successfully');
      setDeleteDialogOpen(false);
      setOfferingToDelete(null);
    } catch (error) {
      toast.error('Failed to delete offering');
    }
  };

  const isLoading = isLoadingCompounds || isLoadingOfferings;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Products</h1>
          <p className="text-secondary-500 dark:text-secondary-400">
            Configure available offerings for your patients
          </p>
        </div>
        <Button
          leftIcon={<PlusIcon className="h-5 w-5" />}
          onClick={handleAddOffering}
        >
          Add Offering
        </Button>
      </div>

      {/* Offerings Grouped by Compound */}
      {isLoading ? (
        <Card className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </Card>
      ) : offerings.length === 0 ? (
        <Card padding="none">
          <EmptyState
            title="No offerings found"
            description="Create offerings based on available compounds. You can customize pricing, quantity, descriptions, and images for each offering."
            action={
              <Button
                leftIcon={<PlusIcon className="h-5 w-5" />}
                onClick={handleAddOffering}
              >
                Add Your First Offering
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {offeringsByCompound.map(({ compoundId, compoundName, offerings: compoundOfferings, activeCount }) => (
            <Card key={compoundId} padding="none">
              {/* Compound Header */}
              <button
                onClick={() => toggleSection(compoundId)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isSectionCollapsed(compoundId) ? (
                    <ChevronRightIcon className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50">{compoundName}</h3>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      {compoundOfferings.length} offering{compoundOfferings.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <Badge
                  variant={activeCount > 0 ? 'success' : 'secondary'}
                  className="text-sm"
                >
                  {activeCount} active
                </Badge>
              </button>

              {/* Offerings Table */}
              {!isSectionCollapsed(compoundId) && (
                <div className="border-t border-secondary-200 dark:border-secondary-700">
                  <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                    <thead className="bg-secondary-50 dark:bg-secondary-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                          Offering
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                          Dosage
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                          Pricing
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-100 dark:divide-secondary-700">
                      {compoundOfferings.map((offering) => (
                        <tr key={offering.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors">
                          {/* Offering Info */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center overflow-hidden shrink-0">
                                {offering.offeringImageUrl ? (
                                  <img
                                    src={offering.offeringImageUrl}
                                    alt={offering.offeringName}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <PhotoIcon className="h-6 w-6 text-secondary-400 dark:text-secondary-500" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-secondary-900 dark:text-secondary-100 truncate max-w-xs">
                                  {offering.offeringName}
                                </p>
                                <p className="text-sm text-secondary-500 dark:text-secondary-400 truncate max-w-xs">
                                  {offering.offeringDescription}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Dosage */}
                          <td className="px-6 py-4">
                            <p className="text-secondary-900 dark:text-secondary-100 truncate max-w-[150px]">
                              {offering.dosageVariety
                                ? `${offering.dosageVariety.dosage} - ${offering.dosageVariety.form}`
                                : '-'
                              }
                            </p>
                          </td>

                          {/* Pricing */}
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-secondary-900 dark:text-secondary-100">
                                {formatCurrency(offering.price)}
                              </p>
                              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                                {offering.monthSupply > 0
                                  ? `${offering.monthSupply} month supply`
                                  : 'One-time purchase'}
                              </p>
                            </div>
                          </td>

                          {/* Quantity */}
                          <td className="px-6 py-4">
                            <span className="text-secondary-900 dark:text-secondary-100">{offering.quantity}</span>
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4">
                            <Badge variant={offering.isActive ? 'success' : 'secondary'} dot>
                              {offering.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleToggleActive(offering)}
                                disabled={isUpdating}
                                className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 disabled:opacity-50"
                                title={offering.isActive ? 'Deactivate' : 'Activate'}
                              >
                                {offering.isActive ? (
                                  <EyeSlashIcon className="h-4 w-4" />
                                ) : (
                                  <EyeIcon className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleEditOffering(offering)}
                                className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors text-primary-500 hover:text-primary-700 dark:hover:text-primary-400"
                                title="Edit"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(offering)}
                                disabled={isDeleting}
                                className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors text-danger-500 hover:text-danger-700 dark:hover:text-danger-400 disabled:opacity-50"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddOfferingModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        compounds={compounds}
        offering={editingOffering}
        isLoading={isUpdating}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Offering"
        message={`Are you sure you want to delete "${offeringToDelete?.offeringName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Medications;
